import React, { useEffect, useRef } from 'react';

interface GardenButterfliesProps {
  isNight: boolean;
}

// Puntos de control botánicos y armoniosos para la trayectoria dentro del jardín
// Coordenadas normalizadas [x, y] donde 0 es el inicio y 1 es el fin del contenedor
const BLUE_WAYPOINTS: [number, number][] = [
  [0.20, 0.65], // Tulipanes izquierdos (néctar floral a baja altura)
  [0.32, 0.55], // Ascenso suave sobre el cantero de flores
  [0.46, 0.36], // Corriente térmica hacia el cielo central
  [0.64, 0.25], // Planeo alto soleado en la parte superior
  [0.78, 0.42], // Curva descendente amplia hacia el lado derecho
  [0.82, 0.62], // Revoloteo rasante sobre los tulipanes derechos
  [0.68, 0.68], // Vuelo bajo y suave sobre el césped
  [0.44, 0.64], // Cruce sobre la pradera hacia la izquierda
];

const PINK_WAYPOINTS: [number, number][] = [
  [0.80, 0.63], // Tulipanes derechos
  [0.64, 0.54], // Planeo suave cruzando hacia el centro
  [0.44, 0.38], // Ascenso hacia el cielo
  [0.26, 0.26], // Vuelo alto en la parte superior izquierda
  [0.16, 0.45], // Curva descendente hacia el cantero izquierdo
  [0.24, 0.66], // Revoloteo rasante sobre los pétalos izquierdos
  [0.48, 0.67], // Vuelo bajo sobre el prado central
  [0.70, 0.65], // Retorno suave a las flores de partida
];

// Interpolación cúbica Catmull-Rom para trayectorias continuas y 100% orgánicas (C1 continuidad)
function catmullRom(p0: number, p1: number, p2: number, p3: number, t: number): number {
  return 0.5 * (
    (2 * p1) +
    (-p0 + p2) * t +
    (2 * p0 - 5 * p1 + 4 * p2 - p3) * t * t +
    (-p0 + 3 * p1 - 3 * p2 + p3) * t * t * t
  );
}

// Derivada analítica del spline Catmull-Rom para obtener el vector de velocidad tangente exacto
function catmullRomDerivative(p0: number, p1: number, p2: number, p3: number, t: number): number {
  return 0.5 * (
    (-p0 + p2) +
    2 * (2 * p0 - 5 * p1 + 4 * p2 - p3) * t +
    3 * (-p0 + 3 * p1 - 3 * p2 + p3) * t * t
  );
}

// Evaluación del spline cerrado a lo largo de un parámetro t en [0, 1]
function evaluateClosedSpline(
  waypoints: [number, number][],
  progress: number
): { x: number; y: number; dx: number; dy: number } {
  const n = waypoints.length;
  // Progreso en el rango [0, n]
  const p = ((progress % 1) + 1) % 1 * n;
  const index = Math.floor(p);
  const localT = p - index;

  const i0 = (index - 1 + n) % n;
  const i1 = index;
  const i2 = (index + 1) % n;
  const i3 = (index + 2) % n;

  const p0 = waypoints[i0];
  const p1 = waypoints[i1];
  const p2 = waypoints[i2];
  const p3 = waypoints[i3];

  const x = catmullRom(p0[0], p1[0], p2[0], p3[0], localT);
  const y = catmullRom(p0[1], p1[1], p2[1], p3[1], localT);
  const dx = catmullRomDerivative(p0[0], p1[0], p2[0], p3[0], localT);
  const dy = catmullRomDerivative(p0[1], p1[1], p2[1], p3[1], localT);

  return { x, y, dx, dy };
}

// Interpolación angular más corta (evita giros innecesarios de 360 grados)
function lerpAngle(current: number, target: number, factor: number): number {
  let diff = (target - current) % 360;
  if (diff < -180) diff += 360;
  if (diff > 180) diff -= 360;
  return current + diff * factor;
}

export const GardenButterflies: React.FC<GardenButterfliesProps> = React.memo(({ isNight }) => {
  const blueRef = useRef<HTMLDivElement | null>(null);
  const pinkRef = useRef<HTMLDivElement | null>(null);

  // Animación continua y fluida mediante requestAnimationFrame directa sobre el DOM
  useEffect(() => {
    if (isNight) return;

    let animId: number;
    let startTime: number | null = null;
    let blueAngle = 35;
    let pinkAngle = -45;

    const BLUE_DURATION = 28000; // 28 segundos por vuelta completa (vuelo sereno)
    const PINK_DURATION = 25000; // 25 segundos por vuelta completa (ritmo armónico)

    const tick = (now: number) => {
      if (!startTime) startTime = now;
      const elapsed = now - startTime;

      // 1. Mariposa Azul
      if (blueRef.current) {
        const progressBlue = (elapsed % BLUE_DURATION) / BLUE_DURATION;
        const stateBlue = evaluateClosedSpline(BLUE_WAYPOINTS, progressBlue);

        // Vector tangente de dirección
        const targetAngleBlue = (Math.atan2(stateBlue.dy, stateBlue.dx) * 180) / Math.PI + 90;
        blueAngle = lerpAngle(blueAngle, targetAngleBlue, 0.07);

        // Micro-ondulación orgánica de sustentación aerodinámica
        const microLift = Math.sin(elapsed * 0.0035) * 4;

        blueRef.current.style.left = `${(stateBlue.x * 100).toFixed(3)}%`;
        blueRef.current.style.top = `calc(${(stateBlue.y * 100).toFixed(3)}% + ${microLift.toFixed(1)}px)`;
        blueRef.current.style.transform = `translate(-50%, -50%) rotate(${blueAngle.toFixed(1)}deg)`;
      }

      // 2. Mariposa Rosa
      if (pinkRef.current) {
        const progressPink = (elapsed % PINK_DURATION) / PINK_DURATION;
        const statePink = evaluateClosedSpline(PINK_WAYPOINTS, progressPink);

        const targetAnglePink = (Math.atan2(statePink.dy, statePink.dx) * 180) / Math.PI + 90;
        pinkAngle = lerpAngle(pinkAngle, targetAnglePink, 0.07);

        const microLiftPink = Math.sin(elapsed * 0.0038 + 1.2) * 4;

        pinkRef.current.style.left = `${(statePink.x * 100).toFixed(3)}%`;
        pinkRef.current.style.top = `calc(${(statePink.y * 100).toFixed(3)}% + ${microLiftPink.toFixed(1)}px)`;
        pinkRef.current.style.transform = `translate(-50%, -50%) rotate(${pinkAngle.toFixed(1)}deg)`;
      }

      animId = requestAnimationFrame(tick);
    };

    animId = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(animId);
    };
  }, [isNight]);

  if (isNight) return null;

  return (
    <div
      className="absolute inset-0 pointer-events-none z-25 overflow-hidden select-none"
      id="garden-butterflies-layer"
      aria-hidden="true"
    >
      <style>{`
        /* ========================================================================= */
        /* ALETEO Y FÍSICA ANATÓMICA EXACTA DE SECNOVA.SHOP                          */
        /* ========================================================================= */

        .m-svg {
          display: block;
          overflow: visible;
          filter: drop-shadow(0 3px 6px rgba(40, 25, 10, 0.22));
        }

        /* Aleteo con pliegue exacto sobre el torso del SVG */
        .m-svg .ala {
          transform-box: view-box;
          transform-origin: 52px 40px; /* justo sobre el torso */
          animation: aletearSecnova 0.28s ease-in-out infinite;
          will-change: transform;
        }

        .m-svg .ala-pink {
          transform-box: view-box;
          transform-origin: 52px 40px;
          animation: aletearSecnova 0.26s ease-in-out infinite;
          will-change: transform;
        }

        @keyframes aletearSecnova {
          0%, 100% {
            transform: scaleX(0.16) scaleY(0.93);
          }
          50% {
            transform: scaleX(1) scaleY(1);
          }
        }

        /* Altibajos naturales del vuelo (Fiel a secnova.shop) */
        .m-alto-blue {
          animation: volarYSecnova 2.6s ease-in-out infinite;
          will-change: transform;
        }

        .m-alto-pink {
          animation: volarYSecnova 2.4s ease-in-out infinite;
          will-change: transform;
        }

        @keyframes volarYSecnova {
          0%   { transform: translateY(0); }
          19%  { transform: translateY(-19px); }
          37%  { transform: translateY(-5px); }
          56%  { transform: translateY(-25px); }
          73%  { transform: translateY(1px); }
          88%  { transform: translateY(-12px); }
          100% { transform: translateY(0); }
        }

        /* Ladeo orgánico del cuerpo (Fiel a secnova.shop) */
        .m-cuerpo-blue {
          animation: ladeoSecnova 3.4s ease-in-out infinite;
          transform-origin: 50% 55%;
        }

        .m-cuerpo-pink {
          animation: ladeoSecnova 3.1s ease-in-out infinite;
          transform-origin: 50% 55%;
        }

        @keyframes ladeoSecnova {
          0%, 100% { transform: rotate(-13deg); }
          34%      { transform: rotate(9deg); }
          68%      { transform: rotate(-3deg); }
        }
      `}</style>

      {/* ========================================================================= */}
      {/* MARIPOSA 1 DE 2: CELESTE (secnova.shop)                                   */}
      {/* ========================================================================= */}
      <div
        ref={blueRef}
        id="butterfly-bf-blue"
        className="absolute pointer-events-none select-none"
        style={{
          width: '58px',
          height: '46px',
          zIndex: 32,
          willChange: 'left, top, transform',
        }}
      >
        <div className="m-alto-blue">
          <div className="m-cuerpo-blue">
            <svg className="m-svg" width="58" height="46" viewBox="0 0 104 84">
              <defs>
                <linearGradient id="gaBlue" x1="1" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#e8f4ff" />
                  <stop offset="46%" stopColor="#a9cdf2" />
                  <stop offset="100%" stopColor="#5b8fd1" />
                </linearGradient>
                <linearGradient id="gbBlue" x1="1" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#a9cdf2" />
                  <stop offset="70%" stopColor="#5b8fd1" />
                  <stop offset="100%" stopColor="#1f3d66" />
                </linearGradient>
              </defs>

              {/* Ala izquierda con aleteo sobre el torso (secnova.shop) */}
              <g className="ala">
                <path
                  d="M52 36 C 41 12, 22 3, 11 9 C 1 15, 3 32, 15 40 C 27 47, 45 43, 52 39 Z"
                  fill="url(#gaBlue)"
                  stroke="#1f3d66"
                  strokeWidth="2"
                  strokeLinejoin="round"
                />
                <path
                  d="M52 41 C 43 45, 26 47, 18 55 C 10 63, 14 76, 26 78 C 37 79, 48 63, 52 49 Z"
                  fill="url(#gbBlue)"
                  stroke="#1f3d66"
                  strokeWidth="2"
                  strokeLinejoin="round"
                />
                <path
                  d="M50 37 C 39 28, 27 18, 16 14"
                  stroke="#1f3d66"
                  strokeWidth="1"
                  fill="none"
                  opacity="0.4"
                />
                <path
                  d="M50 39 C 39 34, 29 30, 18 28"
                  stroke="#1f3d66"
                  strokeWidth="1"
                  fill="none"
                  opacity="0.35"
                />
                <path
                  d="M50 46 C 41 52, 32 61, 26 70"
                  stroke="#1f3d66"
                  strokeWidth="1"
                  fill="none"
                  opacity="0.35"
                />
                <ellipse cx="24" cy="20" rx="5" ry="4.2" fill="#2f5b91" opacity="0.5" />
                <ellipse cx="15" cy="30" rx="2.8" ry="2.4" fill="#e8f4ff" opacity="0.8" />
                <ellipse cx="27" cy="66" rx="3.4" ry="2.8" fill="#e8f4ff" opacity="0.7" />
              </g>

              {/* Ala derecha envuelta en grupo espejado (secnova.shop) */}
              <g className="ala">
                <g transform="translate(104,0) scale(-1,1)">
                  <path
                    d="M52 36 C 41 12, 22 3, 11 9 C 1 15, 3 32, 15 40 C 27 47, 45 43, 52 39 Z"
                    fill="url(#gaBlue)"
                    stroke="#1f3d66"
                    strokeWidth="2"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M52 41 C 43 45, 26 47, 18 55 C 10 63, 14 76, 26 78 C 37 79, 48 63, 52 49 Z"
                    fill="url(#gbBlue)"
                    stroke="#1f3d66"
                    strokeWidth="2"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M50 37 C 39 28, 27 18, 16 14"
                    stroke="#1f3d66"
                    strokeWidth="1"
                    fill="none"
                    opacity="0.4"
                  />
                  <path
                    d="M50 39 C 39 34, 29 30, 18 28"
                    stroke="#1f3d66"
                    strokeWidth="1"
                    fill="none"
                    opacity="0.35"
                  />
                  <path
                    d="M50 46 C 41 52, 32 61, 26 70"
                    stroke="#1f3d66"
                    strokeWidth="1"
                    fill="none"
                    opacity="0.35"
                  />
                  <ellipse cx="24" cy="20" rx="5" ry="4.2" fill="#2f5b91" opacity="0.5" />
                  <ellipse cx="15" cy="30" rx="2.8" ry="2.4" fill="#e8f4ff" opacity="0.8" />
                  <ellipse cx="27" cy="66" rx="3.4" ry="2.8" fill="#e8f4ff" opacity="0.7" />
                </g>
              </g>

              {/* Cuerpo anatómico superior a las alas */}
              <path
                d="M52 21 C 55.4 21, 56.8 27, 56.8 39 C 56.8 52, 54.8 62, 52 64 C 49.2 62, 47.2 52, 47.2 39 C 47.2 27, 48.6 21, 52 21 Z"
                fill="#3d2410"
              />
              <path
                d="M52 23 C 53.8 23, 54.8 28, 54.8 39 C 54.8 50, 53.8 58, 52 60"
                stroke="#77502c"
                strokeWidth="1.4"
                fill="none"
                opacity="0.65"
              />
              <circle cx="52" cy="19" r="4.8" fill="#2f1b0b" />
              <circle cx="49.8" cy="17.8" r="1.4" fill="#0d0703" />
              <circle cx="54.2" cy="17.8" r="1.4" fill="#0d0703" />
              <path
                d="M50.2 15.4 C 46 9.5, 42 5.5, 37.5 3.4"
                stroke="#2f1b0b"
                strokeWidth="1.6"
                fill="none"
                strokeLinecap="round"
              />
              <path
                d="M53.8 15.4 C 58 9.5, 62 5.5, 66.5 3.4"
                stroke="#2f1b0b"
                strokeWidth="1.6"
                fill="none"
                strokeLinecap="round"
              />
              <circle cx="36.8" cy="3" r="1.9" fill="#2f1b0b" />
              <circle cx="67.2" cy="3" r="1.9" fill="#2f1b0b" />
            </svg>
          </div>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* MARIPOSA 2 DE 2: ROSADA (secnova.shop)                                    */}
      {/* ========================================================================= */}
      <div
        ref={pinkRef}
        id="butterfly-bf-pink"
        className="absolute pointer-events-none select-none"
        style={{
          width: '54px',
          height: '44px',
          zIndex: 32,
          willChange: 'left, top, transform',
        }}
      >
        <div className="m-alto-pink">
          <div className="m-cuerpo-pink">
            <svg className="m-svg" width="54" height="44" viewBox="0 0 104 84">
              <defs>
                <linearGradient id="gaPink" x1="1" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#ffe6ee" />
                  <stop offset="46%" stopColor="#f4a3bb" />
                  <stop offset="100%" stopColor="#d95d84" />
                </linearGradient>
                <linearGradient id="gbPink" x1="1" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#f4a3bb" />
                  <stop offset="70%" stopColor="#d95d84" />
                  <stop offset="100%" stopColor="#6d2340" />
                </linearGradient>
              </defs>

              {/* Ala izquierda */}
              <g className="ala-pink">
                <path
                  d="M52 36 C 41 12, 22 3, 11 9 C 1 15, 3 32, 15 40 C 27 47, 45 43, 52 39 Z"
                  fill="url(#gaPink)"
                  stroke="#6d2340"
                  strokeWidth="2"
                  strokeLinejoin="round"
                />
                <path
                  d="M52 41 C 43 45, 26 47, 18 55 C 10 63, 14 76, 26 78 C 37 79, 48 63, 52 49 Z"
                  fill="url(#gbPink)"
                  stroke="#6d2340"
                  strokeWidth="2"
                  strokeLinejoin="round"
                />
                <path
                  d="M50 37 C 39 28, 27 18, 16 14"
                  stroke="#6d2340"
                  strokeWidth="1"
                  fill="none"
                  opacity="0.4"
                />
                <path
                  d="M50 39 C 39 34, 29 30, 18 28"
                  stroke="#6d2340"
                  strokeWidth="1"
                  fill="none"
                  opacity="0.35"
                />
                <path
                  d="M50 46 C 41 52, 32 61, 26 70"
                  stroke="#6d2340"
                  strokeWidth="1"
                  fill="none"
                  opacity="0.35"
                />
                <ellipse cx="24" cy="20" rx="5" ry="4.2" fill="#8f2f52" opacity="0.5" />
                <ellipse cx="15" cy="30" rx="2.8" ry="2.4" fill="#ffe6ee" opacity="0.8" />
                <ellipse cx="27" cy="66" rx="3.4" ry="2.8" fill="#ffe6ee" opacity="0.7" />
              </g>

              {/* Ala derecha envuelta en grupo espejado */}
              <g className="ala-pink">
                <g transform="translate(104,0) scale(-1,1)">
                  <path
                    d="M52 36 C 41 12, 22 3, 11 9 C 1 15, 3 32, 15 40 C 27 47, 45 43, 52 39 Z"
                    fill="url(#gaPink)"
                    stroke="#6d2340"
                    strokeWidth="2"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M52 41 C 43 45, 26 47, 18 55 C 10 63, 14 76, 26 78 C 37 79, 48 63, 52 49 Z"
                    fill="url(#gbPink)"
                    stroke="#6d2340"
                    strokeWidth="2"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M50 37 C 39 28, 27 18, 16 14"
                    stroke="#6d2340"
                    strokeWidth="1"
                    fill="none"
                    opacity="0.4"
                  />
                  <path
                    d="M50 39 C 39 34, 29 30, 18 28"
                    stroke="#6d2340"
                    strokeWidth="1"
                    fill="none"
                    opacity="0.35"
                  />
                  <path
                    d="M50 46 C 41 52, 32 61, 26 70"
                    stroke="#6d2340"
                    strokeWidth="1"
                    fill="none"
                    opacity="0.35"
                  />
                  <ellipse cx="24" cy="20" rx="5" ry="4.2" fill="#8f2f52" opacity="0.5" />
                  <ellipse cx="15" cy="30" rx="2.8" ry="2.4" fill="#ffe6ee" opacity="0.8" />
                  <ellipse cx="27" cy="66" rx="3.4" ry="2.8" fill="#ffe6ee" opacity="0.7" />
                </g>
              </g>

              {/* Cuerpo anatómico */}
              <path
                d="M52 21 C 55.4 21, 56.8 27, 56.8 39 C 56.8 52, 54.8 62, 52 64 C 49.2 62, 47.2 52, 47.2 39 C 47.2 27, 48.6 21, 52 21 Z"
                fill="#3d2410"
              />
              <path
                d="M52 23 C 53.8 23, 54.8 28, 54.8 39 C 54.8 50, 53.8 58, 52 60"
                stroke="#77502c"
                strokeWidth="1.4"
                fill="none"
                opacity="0.65"
              />
              <circle cx="52" cy="19" r="4.8" fill="#2f1b0b" />
              <circle cx="49.8" cy="17.8" r="1.4" fill="#0d0703" />
              <circle cx="54.2" cy="17.8" r="1.4" fill="#0d0703" />
              <path
                d="M50.2 15.4 C 46 9.5, 42 5.5, 37.5 3.4"
                stroke="#2f1b0b"
                strokeWidth="1.6"
                fill="none"
                strokeLinecap="round"
              />
              <path
                d="M53.8 15.4 C 58 9.5, 62 5.5, 66.5 3.4"
                stroke="#2f1b0b"
                strokeWidth="1.6"
                fill="none"
                strokeLinecap="round"
              />
              <circle cx="36.8" cy="3" r="1.9" fill="#2f1b0b" />
              <circle cx="67.2" cy="3" r="1.9" fill="#2f1b0b" />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
});

GardenButterflies.displayName = 'GardenButterflies';
